import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Clock, HelpCircle, Loader2, ShieldAlert, XCircle } from 'lucide-react';

const LOOK = {
  loading: { icon: Loader2, color: 'text-slate-500 animate-spin', bg: 'bg-slate-50', title: 'swishReturn.loadingTitle', desc: 'swishReturn.loadingDesc' },
  success: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', title: 'swishReturn.successTitle', desc: 'swishReturn.successDesc' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', title: 'swishReturn.failedTitle', desc: 'swishReturn.failedDesc' },
  expired: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', title: 'swishReturn.expiredTitle', desc: 'swishReturn.expiredDesc' },
  pending: { icon: Clock, color: 'text-amber-600 animate-pulse', bg: 'bg-amber-50', title: 'swishReturn.pendingTitle', desc: 'swishReturn.pendingDesc' },
  suspicious: { icon: ShieldAlert, color: 'text-red-700', bg: 'bg-red-100', title: 'swishReturn.suspiciousTitle', desc: 'swishReturn.suspiciousHint' },
  notFound: { icon: HelpCircle, color: 'text-slate-500', bg: 'bg-slate-50', title: 'swishReturn.notFoundTitle', desc: 'swishReturn.notFoundDesc' },
  unknown: { icon: HelpCircle, color: 'text-slate-500', bg: 'bg-slate-50', title: 'swishReturn.unknownTitle', desc: 'swishReturn.unknownDesc' },
};

const SUSPICIOUS_COPY = { payee: 'swishReturn.suspiciousPayee', amount: 'swishReturn.suspiciousAmount', other: 'swishReturn.suspiciousOther' };

function DetailRows({ t, details }) {
  if (!details || (details.amount == null && !details.recipient)) return null;
  return (
    <div className="rounded-lg border bg-white p-3 text-left text-sm space-y-1" data-testid="payment-return-info">
      {details.amount != null && (
        <p data-testid="payment-return-amount"><span className="text-muted-foreground">{t('swishReturn.amount')}:</span> {details.amount} {details.currency || 'SEK'}</p>
      )}
      {details.recipient && (
        <p data-testid="payment-return-recipient"><span className="text-muted-foreground">{t('swishReturn.to')}:</span> {details.recipient}</p>
      )}
      {details.reference && (
        <p className="text-xs text-muted-foreground" data-testid="payment-return-reference">{t('swishReturn.reference')}: {details.reference}</p>
      )}
    </div>
  );
}

// Return screen for every Swish outcome. No manual confirmation control by design (V6.2.3).
export function SwishReturnCard({ t, state, reason, details, ticketReported, onHome, onRetry, onLogin, extraActions }) {
  const look = LOOK[state] || LOOK.unknown;
  const Icon = look.icon;
  return (
    <div className={`min-h-screen ${look.bg} flex items-center justify-center p-4 transition-colors duration-300`}>
      <Card className="w-full max-w-md shadow-lg" data-testid="payment-return-page" data-state={state}>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4"><Icon className={`h-16 w-16 ${look.color}`} /></div>
          <CardTitle className="text-xl" data-testid="payment-return-title">{t(look.title)}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {state === 'suspicious' && (
            <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-left text-sm text-red-800" data-testid="verification-failed-banner">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t(SUSPICIOUS_COPY[reason] || SUSPICIOUS_COPY.other)}</span>
            </div>
          )}
          {state === 'pending' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800" data-testid="payment-pending-badge">
              <Clock className="h-3 w-3" /> {t('swishReturn.pendingBadge')}
            </span>
          )}
          <p className="text-sm text-muted-foreground" data-testid="payment-return-desc">{t(look.desc)}</p>
          <DetailRows t={t} details={details} />
          {ticketReported && (
            <p className="text-xs text-amber-800" data-testid="support-notified-note">{t('swishReturn.supportNotified')}</p>
          )}
          <div className="flex flex-col gap-2 pt-2">
            {state === 'failed' && onRetry && (
              <Button onClick={onRetry} data-testid="retry-payment-btn">{t('swishReturn.retry')}</Button>
            )}
            {state === 'unknown' && onLogin && (
              <Button onClick={onLogin} data-testid="login-btn">{t('swishReturn.login')}</Button>
            )}
            {extraActions}
            {state !== 'loading' && onHome && (
              <Button variant="outline" onClick={onHome} data-testid="return-home-btn">{t('swishReturn.returnHome')}</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
